/*
 * My Templates — brand templates the user saves from the Upload flow. Kept
 * entirely client-side (localStorage) since there's no backend to own a
 * per-user template library yet: "Yours to manage, not your organisation."
 * Stores the full extracted BrandSpec so a saved row can be re-applied
 * synchronously later (setDirectBrand), with no re-download/re-parse.
 */

import { getErrorMessage } from "../../shared/services/connectorErrors";
import { logError, scrubMessage } from "../../../../../telemetry";
import type { BrandSpec } from "../../../types";

const STORAGE_KEY = "deckorate.applyBrand.myTemplates";
/** Sane cap on how many local templates persist — oldest entries are
 *  dropped first so localStorage can't grow unbounded. */
const MAX_MY_BRAND_TEMPLATES = 50;
const MY_BRAND_TEMPLATES_CONNECTOR = "deckorate";

export interface MyBrandTemplate {
  readonly id: string;
  readonly name: string;
  readonly brand: BrandSpec;
  /** Row-preview swatches, captured at save time. */
  readonly colors: readonly string[];
  /** ISO timestamp — newest-first ordering. */
  readonly savedAt: string;
}

export function logMyBrandTemplatesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "MyBrandTemplatesStorageError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: MY_BRAND_TEMPLATES_CONNECTOR, reason },
  );
}

// Math.random() here only needs to be unique among this browser's own local
// templates, not unpredictable — it's a localStorage row key, never a
// security token — so the non-cryptographic PRNG is safe.
function generateFallbackId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`; // NOSONAR typescript:S2245
}

function generateId(): string {
  const uuid = globalThis.crypto?.randomUUID?.() ?? generateFallbackId();
  return `local-${uuid}`;
}

/** Light structural check — enough to keep a corrupted/foreign localStorage
 *  value from crashing the list, without deep-validating every BrandSpec field. */
function isValidEntry(value: unknown): value is MyBrandTemplate {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === "string" && entry.id.length > 0 &&
    typeof entry.name === "string" && entry.name.length > 0 &&
    typeof entry.brand === "object" && entry.brand !== null &&
    Array.isArray(entry.colors) && entry.colors.every((c) => typeof c === "string") &&
    typeof entry.savedAt === "string"
  );
}

function readAll(): MyBrandTemplate[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry);
  } catch (error) {
    logMyBrandTemplatesError("my-brand-templates-read", getErrorMessage(error, "Couldn't read saved templates from storage."));
    return [];
  }
}

function writeAll(templates: readonly MyBrandTemplate[]): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

/** Returns every locally-saved template, newest first. Never throws —
 *  degrades to an empty list on any storage/parsing failure. */
export function listMyBrandTemplates(): MyBrandTemplate[] {
  return readAll();
}

/**
 * Persists a new local template and returns it. Throws on failure (storage
 * unavailable, quota exceeded) so the caller can surface an error instead of
 * silently claiming success.
 */
export function saveMyBrandTemplate(name: string, brand: BrandSpec, colors: readonly string[]): MyBrandTemplate {
  const trimmedName = name.trim() || "Untitled Template";
  const entry: MyBrandTemplate = { id: generateId(), name: trimmedName, brand, colors, savedAt: new Date().toISOString() };
  try {
    const next = [entry, ...readAll()].slice(0, MAX_MY_BRAND_TEMPLATES);
    writeAll(next);
    return entry;
  } catch (error) {
    const message = getErrorMessage(error, "Couldn't save this template to local storage.");
    logMyBrandTemplatesError("my-brand-templates-save", message);
    throw new Error(message);
  }
}

/** Removes a local template by id. Never throws — a failed removal is
 *  logged but otherwise harmless (the row just stays listed). */
export function removeMyBrandTemplate(id: string): void {
  try {
    writeAll(readAll().filter((t) => t.id !== id));
  } catch (error) {
    logMyBrandTemplatesError("my-brand-templates-remove", getErrorMessage(error, "Couldn't remove this template from storage."));
  }
}
